import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import Button from "@/components/Button";
import Input from "@/components/Input";
import DatePicker from "@/components/DatePicker";
import { pb } from "@/lib/pocketbase";
import { z } from "zod";

const getCompleteProfileSchema = (
  hasUsername: boolean,
  hasBirthdate: boolean,
  hasGender: boolean
) => {
  return z.object({
    birthdate: hasBirthdate
      ? z.string().optional()
      : z
          .string()
          .min(1, "Birthdate is required")
          .refine((date) => {
            const selectedDate = new Date(date);
            const today = new Date();
            const minDate = new Date("1900-01-01");
            return selectedDate <= today && selectedDate >= minDate;
          }, "Please enter a valid birthdate"),
    username: hasUsername
      ? z.string().optional()
      : z
          .string()
          .min(1, "Username is required")
          .min(3, "Username must be at least 3 characters long")
          .regex(
            /^[a-zA-Z0-9_-]+$/,
            "Username can only contain letters, numbers, underscores, and hyphens"
          ),
    gender: hasGender
      ? z.enum(["male", "female"]).optional()
      : z.enum(["male", "female"], {
          required_error: "Please select an avatar",
        }),
  });
};

const CompleteProfilePage = () => {
  const [birthdate, setBirthdate] = useState("");
  const [username, setUsername] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    birthdate?: string;
    username?: string;
    gender?: string;
  }>({});
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const user = pb.authStore.model;

  // Pre-populate form with existing user data ONCE on mount
  useEffect(() => {
    if (user) {
      // Set username if it exists
      if (user.username) {
        setUsername(user.username);
      }

      // Set birthdate if it exists (convert from ISO to YYYY-MM-DD format)
      if (user.birthdate) {
        const date = new Date(user.birthdate);
        const formattedDate = date.toISOString().split("T")[0];
        setBirthdate(formattedDate);
      }

      // Set gender if it exists
      if (user.gender) {
        setGender(user.gender);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, not when user changes

  // Warn user before closing/refreshing page with incomplete profile
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if profile is incomplete
      const isProfileIncomplete = !user?.birthdate || !user?.gender;

      if (isProfileIncomplete && !isSubmitting) {
        e.preventDefault();
        e.returnValue = "Please complete your profile before leaving";
        return "Please complete your profile before leaving";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user, isSubmitting]);

  // Show notification when user tries to navigate to other routes
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (link && link.href) {
        // Check if it's an internal navigation link (not external)
        const isInternalLink = link.href.startsWith(window.location.origin);
        const isProfileIncomplete = !user?.birthdate || !user?.gender;

        if (isInternalLink && isProfileIncomplete && !isSubmitting) {
          // Show a notification when they try to navigate
          setTimeout(() => {
            enqueueSnackbar(
              "Please complete your profile to access other pages",
              {
                variant: "warning",
                autoHideDuration: 4000,
              }
            );
          }, 100);
        }
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [user, isSubmitting, enqueueSnackbar]);

  // Redirect if user already has birthdate and gender
  if (user?.birthdate && user?.gender) {
    navigate({ to: "/dashboard" });
    return null;
  }

  // Redirect to sign in if not authenticated
  if (!user) {
    navigate({ to: "/sign-in" });
    return null;
  }

  // Users on this page are Google sign-in users who need to complete their profile

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    try {
      const formData = {
        birthdate,
        username,
        gender: gender as "male" | "female",
      };

      // Create dynamic schema based on what user already has
      const schema = getCompleteProfileSchema(
        !!user?.username,
        !!user?.birthdate,
        !!user?.gender
      );

      schema.parse(formData);
      setValidationErrors({});
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: typeof validationErrors = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            errors[error.path[0] as keyof typeof errors] = error.message;
          }
        });
        setValidationErrors(errors);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Format birthdate to ISO string
      const formattedBirthdate = new Date(
        birthdate + "T00:00:00.000Z"
      ).toISOString();

      // Prepare update data
      const updateData: any = {
        birthdate: formattedBirthdate,
        username,
        gender,
      };

      // Update user profile
      await pb.collection("users").update(user.id, updateData);

      // Update the auth store with the new user data
      pb.authStore.save(pb.authStore.token, {
        ...user,
        birthdate: formattedBirthdate,
        username,
        gender,
      });

      enqueueSnackbar("Profile completed successfully!", {
        variant: "success",
      });
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      // Handle PocketBase specific errors
      if (err.data && typeof err.data === "object") {
        const fieldErrors: typeof validationErrors = {};
        const errorMessages: string[] = [];

        Object.keys(err.data).forEach((field) => {
          const fieldError = err.data[field];
          if (
            fieldError &&
            typeof fieldError === "object" &&
            fieldError.message
          ) {
            fieldErrors[field as keyof typeof validationErrors] =
              fieldError.message;
            errorMessages.push(fieldError.message);
          }
        });

        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors);
          enqueueSnackbar(errorMessages[0] || "Validation error", {
            variant: "error",
          });
        } else {
          enqueueSnackbar(err.message || "Failed to update profile", {
            variant: "error",
          });
        }
      } else {
        enqueueSnackbar(err.message || "Failed to update profile", {
          variant: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChange = (date: string) => {
    setBirthdate(date);
    if (validationErrors.birthdate) {
      setValidationErrors((prev) => ({ ...prev, birthdate: undefined }));
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (validationErrors.username) {
      setValidationErrors((prev) => ({ ...prev, username: undefined }));
    }
  };

  return (
    <div className="pt-24 min-h-screen flex items-center justify-center bg-base-200 p-4">
      <div className="card w-full max-w-md bg-white shadow-xl">
        <div className="card-body p-8">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Complete Your Profile
          </h2>
          <p className="text-gray-600 mb-8 text-center">
            We need a bit more information to personalize your learning
            experience.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                key={`username-${user?.id || "new"}`}
                type="text"
                label="Username"
                value={username}
                onChange={handleUsernameChange}
                error={validationErrors.username}
                placeholder="Choose a unique username"
                autoComplete="off"
              />
              <p className="text-sm text-gray-500 mt-1">
                This will be your unique identifier on the platform
              </p>
            </div>

            <div>
              <DatePicker
                label="Birthdate"
                value={birthdate}
                onChange={handleDateChange}
                error={validationErrors.birthdate || undefined}
                maxDate={new Date()}
                minDate={new Date("1900-01-01")}
                placeholder="Select your birthdate"
              />
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-[18px] font-bold">
                  Select Your Avatar <span className="text-error">*</span>
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  You can change this later from your profile
                </p>
              </div>
              <div className="flex gap-6 justify-center items-center">
                <div className="flex flex-col items-center gap-2">
                  <img
                    src="/assets/default-avatar-m.png"
                    alt="Male avatar"
                    className={`cursor-pointer w-32 h-32 rounded-full transition ${
                      gender === "male"
                        ? "ring-4 ring-primary"
                        : "opacity-60 hover:opacity-100 hover:ring-2 hover:ring-primary/50"
                    }`}
                    onClick={() => {
                      setGender("male");
                      if (validationErrors.gender) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          gender: undefined,
                        }));
                      }
                    }}
                  />
                  <span
                    className={`text-sm font-semibold ${gender === "male" ? "text-primary" : "text-gray-500"}`}
                  >
                    Male
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <img
                    src="/assets/default-avatar-f.png"
                    alt="Female avatar"
                    className={`cursor-pointer w-32 h-32 rounded-full transition ${
                      gender === "female"
                        ? "ring-4 ring-primary"
                        : "opacity-60 hover:opacity-100 hover:ring-2 hover:ring-primary/50"
                    }`}
                    onClick={() => {
                      setGender("female");
                      if (validationErrors.gender) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          gender: undefined,
                        }));
                      }
                    }}
                  />
                  <span
                    className={`text-sm font-semibold ${gender === "female" ? "text-primary" : "text-gray-500"}`}
                  >
                    Female
                  </span>
                </div>
              </div>
              {validationErrors.gender && (
                <p className="text-[16px] font-bold text-error text-center">
                  {validationErrors.gender}
                </p>
              )}

              {/* Avatar customization tip */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <span className="text-2xl">✨</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-purple-900 mb-1">
                      💡 Pro Tip: Make it uniquely yours!
                    </p>
                    <p className="text-sm text-purple-800">
                      After completing your profile, visit your{" "}
                      <span className="font-semibold text-purple-900">
                        Profile page
                      </span>{" "}
                      to customize your avatar with different hairstyles,
                      accessories, and colors!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Why we need your birthdate:
              </p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Recommend age-appropriate reading content</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Personalize your learning experience</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Track your reading progress over time</span>
                </li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full py-3"
              disabled={
                isSubmitting ||
                (!user?.username && !username) ||
                (!user?.birthdate && !birthdate) ||
                (!user?.gender && !gender)
              }
            >
              {isSubmitting ? "Saving..." : "Continue to Dashboard"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Your information is kept secure and private
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
